import React, { useState, useCallback, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useSocial } from "../../contexts/SocialContext";
import * as api from "../../services/api";
import { ChevronLeft, ChevronRight, Link2, Loader2, LogOut, Trash2, UserPlus, Users } from "lucide-react";
import DetailSheet from "../session/generate/DetailSheet";
import { AddRow, DeleteConfirm, Field, Group, ItemRow } from "../profile/ProfileParts";
import Avatar from "./Avatar";
import InviteDialog from "./InviteDialog";
import GroupFeed from "./GroupFeed";

const personRow = "grid w-full items-center gap-2.5 px-3 py-2.5 text-left";

const roleLabel = (role) => (role === "owner" ? "Owner" : role === "admin" ? "Admin" : null);

const RoleTag = ({ role }) =>
  roleLabel(role) ? (
    <span className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground">
      {roleLabel(role)}
    </span>
  ) : (
    <span />
  );

export default function GroupsTab() {
  const { groups, groupsLoading, loadGroups, friends } = useSocial();
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  if (selectedGroup) {
    return <GroupDetailView groupId={selectedGroup} onBack={() => setSelectedGroup(null)} friends={friends} loadGroups={loadGroups} />;
  }

  return (
    <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-5">
      <Group
        title="Your groups"
        footer={
          !groupsLoading && groups.length === 0
            ? "A group shares workouts with just the people in it, like a training crew or a club."
            : null
        }
      >
        {groupsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          groups.map((group) => {
            const count = parseInt(group.member_count, 10) || 0;
            return (
              <ItemRow
                key={group.id}
                Icon={Users}
                title={group.name}
                detail={`${count} ${count === 1 ? "member" : "members"}${group.role === "owner" ? " · You run it" : ""}`}
                tag={group.role === "admin" ? <RoleTag role="admin" /> : null}
                onClick={() => setSelectedGroup(group.id)}
              />
            );
          })
        )}
        <AddRow label="New group" onClick={() => setCreateOpen(true)} />
      </Group>

      <NewGroupSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={async (group) => {
          await loadGroups();
          if (group?.id) setSelectedGroup(group.id);
        }}
      />
    </div>
  );
}

/**
 * A name and an optional description. The Private / Public / Invite only
 * choice went (issue #86): with no way to find a group, every group is joined
 * by invite, so the three behaved the same. New groups are private.
 */
function NewGroupSheet({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName("");
    setDescription("");
  }, [open]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const group = await api.createGroupApi({ name: name.trim(), description: description.trim(), privacy: "private" });
      onOpenChange(false);
      await onCreated(group);
    } catch (err) {
      console.error("Create group failed:", err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <DetailSheet
      open={open}
      onOpenChange={onOpenChange}
      title="New group"
      footer={
        <Button className="h-12 w-full text-base font-semibold" onClick={handleCreate} disabled={creating || !name.trim()}>
          {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create group
        </Button>
      }
    >
      <Field label="Name" htmlFor="group-name">
        <Input id="group-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tuesday Crew" autoFocus />
      </Field>
      <Field label="Description" htmlFor="group-description" aside="Optional">
        <Input
          id="group-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="When and where you train"
        />
      </Field>
      <p className="text-xs leading-snug text-muted-foreground">
        Only people you invite can join. You'll add them next.
      </p>
    </DetailSheet>
  );
}

function GroupDetailView({ groupId, onBack, friends, loadGroups }) {
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLinkOpen, setInviteLinkOpen] = useState(false);
  const [openMember, setOpenMember] = useState(null);
  const [exitOpen, setExitOpen] = useState(false);

  const loadGroup = useCallback(async () => {
    try {
      const data = await api.getGroup(groupId);
      setGroup(data);
    } catch (err) {
      console.error("Load group failed:", err);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    setLoading(true);
    loadGroup();
  }, [loadGroup]);

  const back = (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-0.5 justify-self-start py-0.5 text-sm text-primary"
    >
      <ChevronLeft className="h-4 w-4" /> Groups
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 mt-4">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="mt-4 grid gap-4">
        {back}
        <p className="text-muted-foreground text-center py-8">Group not found</p>
      </div>
    );
  }

  const isOwner = group.membership?.role === "owner";
  const isAdmin = group.membership?.role === "admin" || isOwner;
  const members = group.members || [];

  return (
    <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-5">
      <div className="grid gap-2">
        {back}
        <div className="grid gap-1">
          <h2 className="text-xl font-bold">{group.name}</h2>
          {group.description && <p className="text-sm text-muted-foreground">{group.description}</p>}
        </div>
      </div>

      <Group title={`Members (${members.length})`}>
        {members.map((member) => {
          // Admins manage anyone but the owner (the server enforces the same).
          const manageable = isAdmin && member.role !== "owner" && member.id !== group.membership?.user_id;
          const inner = (
            <>
              <Avatar name={member.name} photo={member.photo_url} />
              <span className="truncate text-sm font-medium">{member.name}</span>
              <RoleTag role={member.role} />
            </>
          );
          return manageable ? (
            <button
              key={member.id}
              type="button"
              onClick={() => setOpenMember(member)}
              className={`${personRow} grid-cols-[2rem_minmax(0,1fr)_auto_1rem] transition-colors hover:bg-muted/30`}
            >
              {inner}
              <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
            </button>
          ) : (
            <div key={member.id} className={`${personRow} grid-cols-[2rem_minmax(0,1fr)_auto]`}>
              {inner}
            </div>
          );
        })}
        {isAdmin && (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className={`${personRow} grid-cols-[2rem_minmax(0,1fr)] py-3 text-sm font-semibold text-primary transition-colors hover:bg-muted/30`}
          >
            <UserPlus className="mx-auto h-[18px] w-[18px]" />
            Invite people
          </button>
        )}
      </Group>

      <GroupFeed groupId={groupId} />

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <button
          type="button"
          onClick={() => setExitOpen(true)}
          className="grid w-full grid-cols-[1.25rem_minmax(0,1fr)] items-center gap-2.5 px-3 py-3 text-left text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
        >
          {isOwner ? <Trash2 className="h-[18px] w-[18px]" /> : <LogOut className="h-[18px] w-[18px]" />}
          {isOwner ? "Delete group" : "Leave group"}
        </button>
      </div>

      <InviteMembersSheet
        open={inviteOpen && !inviteLinkOpen}
        onOpenChange={setInviteOpen}
        onInviteByLink={() => setInviteLinkOpen(true)}
        friends={friends}
        memberIds={members.map((m) => m.id)}
        groupId={groupId}
        onInvited={loadGroup}
      />
      <InviteDialog open={inviteLinkOpen} onOpenChange={setInviteLinkOpen} kind="group" groupId={groupId} />
      <MemberSheet
        member={openMember}
        group={group}
        onClose={() => setOpenMember(null)}
        onRemoved={loadGroup}
      />
      <ExitGroupSheet
        open={exitOpen}
        onOpenChange={setExitOpen}
        group={group}
        isOwner={isOwner}
        onDone={async () => {
          await loadGroups();
          onBack();
        }}
      />
    </div>
  );
}

/** Invite by link (anyone), or pick from your mates who aren't in it yet. */
function InviteMembersSheet({ open, onOpenChange, onInviteByLink, friends, memberIds, groupId, onInvited }) {
  const [busy, setBusy] = useState(null);
  const candidates = friends.filter((f) => !memberIds.includes(f.id));

  const handleInvite = async (userId) => {
    setBusy(userId);
    try {
      await api.inviteToGroup(groupId, userId);
      await onInvited();
    } catch (err) {
      console.error("Invite failed:", err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <DetailSheet open={open} onOpenChange={onOpenChange} title="Invite people">
      <Button variant="outline" className="w-full" onClick={onInviteByLink}>
        <Link2 className="mr-2 h-4 w-4" /> Invite by link
      </Button>
      <Field label="Your mates">
        {candidates.length === 0 ? (
          <p className="text-xs leading-snug text-muted-foreground">
            All your mates are already in this group. Use the link to bring someone new in.
          </p>
        ) : (
          <div className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background">
            {candidates.map((f) => (
              <div key={f.id} className={`${personRow} grid-cols-[2rem_minmax(0,1fr)_auto]`}>
                <Avatar name={f.name} photo={f.photo_url} />
                <span className="truncate text-sm font-medium">{f.name}</span>
                <Button size="sm" variant="secondary" disabled={busy === f.id} onClick={() => handleInvite(f.id)}>
                  {busy === f.id ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Add
                </Button>
              </div>
            ))}
          </div>
        )}
      </Field>
    </DetailSheet>
  );
}

/** One member, for an admin: removing them asks first. */
function MemberSheet({ member, group, onClose, onRemoved }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(member);
  useEffect(() => {
    if (member) setLast(member);
    else setConfirming(false);
  }, [member]);
  const m = member || last;
  if (!m) return null;

  const handleRemove = async () => {
    setBusy(true);
    try {
      await api.removeGroupMember(group.id, m.id);
      onClose();
      await onRemoved();
    } catch (err) {
      console.error("Remove member failed:", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DetailSheet open={Boolean(member)} onOpenChange={(o) => !o && onClose()} title={m.name}>
      <div className="flex items-center gap-3">
        <Avatar name={m.name} photo={m.photo_url} className="h-12 w-12 text-base" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{m.name}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{roleLabel(m.role) || "Member"} of {group.name}</p>
        </div>
      </div>
      <DeleteConfirm
        action="Remove"
        name={`${m.name} from ${group.name}`}
        consequence="They stop seeing the group's posts. They stay your mate."
        confirming={confirming}
        onAsk={() => setConfirming(true)}
        onCancel={() => setConfirming(false)}
        onConfirm={handleRemove}
        busy={busy}
      />
    </DetailSheet>
  );
}

/** Leaving, or for the owner deleting: one confirm, never a single tap. */
function ExitGroupSheet({ open, onOpenChange, group, isOwner, onDone }) {
  const [busy, setBusy] = useState(false);
  const count = group.members?.length || 0;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      if (isOwner) await api.deleteGroupApi(group.id);
      else await api.leaveGroupApi(group.id);
      onOpenChange(false);
      await onDone();
    } catch (err) {
      console.error(isOwner ? "Delete group failed:" : "Leave group failed:", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DetailSheet open={open} onOpenChange={onOpenChange} title={isOwner ? "Delete group" : "Leave group"}>
      <DeleteConfirm
        action={isOwner ? "Delete" : "Leave"}
        name={group.name}
        consequence={
          isOwner
            ? `It goes for all ${count} ${count === 1 ? "member" : "members"}. This can't be undone.`
            : "You'll need a new invite to come back."
        }
        confirming
        onCancel={() => onOpenChange(false)}
        onConfirm={handleConfirm}
        busy={busy}
      />
    </DetailSheet>
  );
}
